import { CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { HStack } from '@chakra-ui/react';
import { Alert, Button, Checkbox, Col, Divider, Form, Input, Modal, Radio, Row } from "antd";
import { Roles } from 'context/auth';
import { StaffUsersProfile } from "hooks/useUsers";
import moment from 'moment';
import { FC, useState } from "react";
import { roleNameToDisplayName } from "util/helpers";
import BreakerLinks from './BreakerLinks';
import useUserUpdate from "./useUserUpdate";

interface UpdateUserForm {
    userId: string;
    emailAddress: string | undefined;
    sendPasswordResetEmail: boolean;
    roles: string[] | undefined;
    active: boolean | undefined;
    initials: string | null;
    vip: boolean;
    breakerUserId: string | null;
    silentUserId: string | null;
    silentAccountTransfers: boolean;
    frozenAccount: boolean;
}

const RoleDivider: FC<{ caption: string }> = ({ caption }) =>
    <Divider orientation='left' style={{ fontWeight: 'normal', fontSize: '10pt' }}>{caption}</Divider>;

const only = (items: string[], source: string[]) => {
    return source.every(x => items.includes(x));
}

const UpdateUserModal = (props: {
    user: StaffUsersProfile,
    onSuccess: () => Promise<void>,
    allowUserFreeze?: boolean
}) => {
    const { updateUser, removeUserTwoFactor, unlockAccount } = useUserUpdate();
    const [visible, setVisible] = useState<boolean>(false);
    const [working, setWorking] = useState<boolean>(false);
    const [isEmailUpdated, setEmailUpdated] = useState<boolean>(false);
    const [mfaRemoved, setMfaRemoved] = useState<boolean>(false);
    const [unlocked, setUnlocked] = useState<boolean>(false);
    const [form] = Form.useForm<UpdateUserForm>();
    const initialValues: UpdateUserForm = {
        userId: props.user.id,
        emailAddress: props.user.email,
        sendPasswordResetEmail: true,
        roles: props.user.roles,
        active: props.user.active,
        initials: props.user.initials,
        vip: props.user.vip,
        breakerUserId: null,
        silentUserId: null,
        silentAccountTransfers: props.user.silentAccountTransfers,
        frozenAccount: props.user.frozen
    };
    const [instaCopied, setInstaCopied] = useState(false);

    const roles = Form.useWatch('roles', form) ?? []

    const isJustUser = only([Roles.User], roles);
    const isNotStaff = only([Roles.User, Roles.Breaker], roles);
    const isAdmin = only([Roles.Admin], roles);
    const isBreaker = roles.length > 0 && only([Roles.Breaker], roles);

    const closeAndResetModal = () => {
        setVisible(false);
        setInstaCopied(false);
        form.resetFields();
    };

    const submitForm = () => {
        form.validateFields().then(async (data) => {
            setWorking(true);

            try {
                await updateUser({
                    userId: data.userId,
                    emailAddress: data.emailAddress!,
                    sendPasswordResetEmail: data.sendPasswordResetEmail,
                    roles: data.roles,
                    // only update the account active flag if it changed
                    active: data.active !== props.user.active
                        ? data.active
                        : undefined,
                    initials: data.initials,
                    updatedInitials: form.isFieldTouched('initials'),
                    vip: props.user.vip !== data.vip
                        ? data.vip
                        : undefined,
                    breakerUserId: data.breakerUserId,
                    silentUserId: data.silentUserId,
                    silentAccountTransfers: props.user.silentAccountTransfers !== data.silentAccountTransfers
                        ? data.silentAccountTransfers
                        : undefined,
                    frozenAccount: data.frozenAccount !== props.user.frozen
                        ? data.frozenAccount
                        : undefined
                });
                setVisible(false);
                await props.onSuccess();
            } finally {
                setWorking(false);
            }
        });
    };

    const removeTwoFactor = async () => {
        try {
            setWorking(true);
            await removeUserTwoFactor(props.user.id);
            setMfaRemoved(true);
        } finally {
            setWorking(false);
        }
    }

    const unlockUser = async () => {
        try {
            setWorking(true);
            await unlockAccount(props.user.id);
            setUnlocked(true);
        } finally {
            setWorking(false);
        }
    }

    return (
        <>
            <Button type="link" onClick={() => setVisible(true)}>
                Edit User
            </Button>
            <Modal
                title={`Edit User - ${props.user.userName}`}
                closable={false}
                keyboard={false}
                maskClosable={false}
                destroyOnClose={true}
                open={visible}
                confirmLoading={working}
                cancelButtonProps={{ disabled: working }}
                okText={working ? "Saving..." : "Update"}
                onOk={submitForm}
                onCancel={closeAndResetModal}
            >
                <Form
                    form={form}
                    disabled={working}
                    initialValues={initialValues}
                    layout="vertical"
                >
                    {props.user.lockedOutUntil && !unlocked && <Alert
                        type="error"
                        description={`Locked out for the next ${moment(props.user.lockedOutUntil).diff(moment().utc(), 'minutes')} minute(s)`}
                        action={<Button size="small" onClick={unlockUser}>
                            Unlock
                        </Button>}
                        style={{ marginBottom: '10px' }}
                    />}
                    {unlocked && <Alert
                        type="info"
                        description="Account has been unlocked"
                        style={{ marginBottom: '10px' }}
                    />}
                    <Form.Item
                        label="Email Address"
                        name="emailAddress"
                        rules={[{ type: "email", required: true }]}
                    >
                        <Input
                            onChange={(e) =>
                                setEmailUpdated(
                                    e.target.value !==
                                    initialValues.emailAddress
                                )
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        label="Instagram"
                        style={{ borderBottom: '1px solid #d9d9d9' }}
                    >

                        {props.user.instagramHandle ? (
                            <HStack>
                                <span style={{ flex: 1 }}>@{props.user.instagramHandle}</span>
                                <Button
                                    type="link"
                                    icon={instaCopied ? <CheckCircleOutlined /> : <CopyOutlined />}
                                    onClick={() => {
                                        navigator.clipboard.writeText(`@${props.user.instagramHandle}`);
                                        setInstaCopied(true);
                                    }}
                                />
                            </HStack>
                        ) : (
                            <></>
                        )}

                    </Form.Item>
                    <HStack alignItems='baseline' spacing={20}>
                        <Form.Item
                            name="sendPasswordResetEmail"
                            valuePropName="checked"
                        >
                            <Checkbox disabled={!isEmailUpdated}>
                                Send Password Reset Email
                            </Checkbox>
                        </Form.Item>
                        {!props.user.twoFactorEnabled && <div>User has not enabled MFA</div>}
                        {props.user.twoFactorEnabled && !mfaRemoved && <Button
                            danger
                            type="primary"
                            disabled={isAdmin}
                            onClick={removeTwoFactor}
                            loading={working}>
                            Remove MFA
                        </Button>}
                        {mfaRemoved && <div>MFA has been removed</div>}
                    </HStack>
                    <Form.Item label="Roles" name="roles">
                        <Checkbox.Group style={{ width: '100%' }}>
                            <Row>
                                <Col span={24}><RoleDivider caption="Site Administration" /></Col>
                                <Col span={6}><Checkbox value={Roles.Admin}>{roleNameToDisplayName(Roles.Admin)}</Checkbox></Col>
                                <Col span={8}><Checkbox value={Roles.SeniorStaff}>{roleNameToDisplayName(Roles.SeniorStaff)}</Checkbox></Col>

                                <Col span={24}><RoleDivider caption="Processing" /></Col>
                                <Col span={6}><Checkbox value={Roles.Staff}>{roleNameToDisplayName(Roles.Staff)}</Checkbox></Col>
                                <Col span={8}><Checkbox value={Roles.Typer}>{roleNameToDisplayName(Roles.Typer)}</Checkbox></Col>

                                <Col span={24}><RoleDivider caption="Customers" /></Col>
                                <Col span={6}><Checkbox value={Roles.User}>{roleNameToDisplayName(Roles.User)}</Checkbox></Col>
                                <Col span={8}><Checkbox value={Roles.Breaker}>{roleNameToDisplayName(Roles.Breaker)}</Checkbox></Col>
                            </Row>
                        </Checkbox.Group>
                    </Form.Item>
                    <HStack alignItems='baseline' spacing={3}>
                        <Form.Item label="Account Status" name="active">
                            <Radio.Group>
                                <Radio value={true}>Active</Radio>
                                <Radio value={false}>Inactive</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Freeze" name="frozenAccount" valuePropName="checked">
                            <Checkbox disabled={!props.allowUserFreeze} />
                        </Form.Item>
                        <Form.Item label="VIP" name="vip" valuePropName="checked">
                            <Checkbox disabled={!isNotStaff} />
                        </Form.Item>
                        <Form.Item label="Initials Override" name="initials" rules={[{ required: false }]}>
                            <Input disabled={isNotStaff} placeholder='For card folder names' className='initials-override' />
                        </Form.Item>
                    </HStack>
                    <Form.Item hidden name="userId">
                        <Input type="hidden" />
                    </Form.Item>

                    {isJustUser && <BreakerLinks key={props.user.id} userId={props.user.id} />}

                    {isBreaker && <Form.Item
                        name="silentAccountTransfers"
                        help="Allows users under this breaker to transfer funds from their silent account to their consignment account"
                        valuePropName="checked"
                    >
                        <Checkbox>Allow Account Transfers</Checkbox>
                    </Form.Item>}
                </Form>
            </Modal>
        </>
    );
};

export default UpdateUserModal;
